import CarotekColors from "@carotek/utils/Colors";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    view: state.pages.productList.view,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
    stockMsgContainer?: GridItemProps;
    stockMsg?: TypographyPresentationProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;

            [class^="GridItemStyle"] {
                padding-top: 0;
                padding-bottom: 0;
            }
        `,
    },
    brandGridItem: {
        width: 12,
        css: css`
            margin: 10px 0 !important;
        `,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            margin-bottom: 5px !important;
        `,
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            margin: 10px 0 !important;
        `,
    },
    swatchGridItem: {
        width: 12,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    availabilityGridItem: {
        width: 12,
    },
    attributesGridItem: {
        width: 12,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    brandStyles: {
        nameText: {
            size: 14,
            color: `${CarotekColors.blue1}`,
            weight: 400,
            css: css`
                :hover {
                    text-decoration: underline;
                    color: ${CarotekColors.blue1} !important;
                }
            `,
        },
        nameLink: {
            typographyProps: {
                size: 14,
                color: `${CarotekColors.blue1}`,
                weight: 400,
                css: css`
                    :hover {
                        text-decoration: underline;
                        color: ${CarotekColors.blue1} !important;
                    }
                `,
            },
        },
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 14,
                color: `${CarotekColors.blue1}`,
                weight: 700,
                fontFamily: "OpenSansBold",
                lineHeight: "25.6px",
                css: css`
                    :hover {
                        text-decoration: underline;
                        color: ${CarotekColors.blue1} !important;
                    }
                `,
            },
        },
    },
    partNumbersStyles: {
        container: {
            css: css`
                ${GridOffset} {
                    justify-content: flex-start;
                }

                [class^="TypographyStyle"] {
                    font-size: 13px;
                    color: ${CarotekColors.grey13};
                    font-weight: 400;
                }
            `,
        },
        erpNumberGridItem: {
            css: css`
                flex-basis: auto !important;
                width: auto !important;
            `,
        },
        manufacturerItemGridItem: {
            css: css`
                flex-basis: auto !important;
                width: auto !important;
            `,
        },
        erpNumberLabelText: {
            css: css`
                display: none;
            `,
        },
        erpNumberValueText: {
            css: css`
                margin-right: 0.9375em;
            `,
        },
        manufacturerItemValueText: {
            css: css`
                margin-right: 0.9375em;
            `,
        },
    },
    stockMsgContainer: {
        width: 12,
        css: css`
            margin: 10px 0 !important;
        `,
    },
    stockMsg: {
        size: 13,
        color: `${CarotekColors.grey13}`,
        weight: 400,
    },
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    view,
}: Props) => {
    const { product } = productContext;
    if (!product) {
        return null;
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    let variantDisplayTrait = null;
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    const showStockValue = product?.properties?.showStock?.toLowerCase();

    let stockMessage = null;
    if (showStockValue === "true") {
        stockMessage = "Usually in Stock";
    } else if (showStockValue) {
        stockMessage = "Lead Time Required";
    }

    return (
        <GridContainer {...styles.container}>
            {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />
                </GridItem>
            )}
            {showPartNumbers && (
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.partNumbersStyles}
                    />
                </GridItem>
            )}
            {stockMessage && view !== "Table" ? (
                <GridItem {...styles.stockMsgContainer}>
                    <Typography {...styles.stockMsg}>{stockMessage}</Typography>
                </GridItem>
            ) : null}
            {variantDisplayTrait && view === "List" && (
                <GridItem {...styles.swatchGridItem} className="p5">
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </GridItem>
            )}
            {showTextBadges && textBadges && view === "List" && (
                <GridItem {...styles.textBadgeGridItem} className="p4">
                    <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                </GridItem>
            )}
            {showImageBadges && imageBadges && view === "List" && (
                <GridItem {...styles.imageBadgeGridItem} className="p3">
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                </GridItem>
            )}
            {showAvailability && view === "List" && (
                <GridItem {...styles.availabilityGridItem} className="p2">
                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                </GridItem>
            )}
            {showAttributes && view === "List" && (
                <GridItem {...styles.attributesGridItem} className="p1">
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
